/* eslint-disable @typescript-eslint/camelcase, react/button-has-type */

/**
 * T01.E03 - Contact Us Bar
 * List of clickable contact methods, including LivePerson click-to-chat.
 *
 * Note that although this is specced as a separate module, it lives
 * within the Footer. As such, it's treated as a 'subcomponent' of the Footer.
 *
 * NOTE:
 *   - PhoneUs component uses the Telephone link field which is extended from
 *   	   the General link field. in the Sitecore editors,
 * 	 - The issue with General link is that it will add a 'http:\\' prefix to the
 *       url value and result in an incorrect href attribute set for the JSS link,
 *       e.g `href="http:\\tel:XXXX"`.
 *   - Therefore decision had made to use Telephone link field with backend solution
 * 		   to send `"url": "tel:XXXX"` as the field value prop to the PhoneUs component.
 *   - In addition, a hack to override href attribute with url value is required
 *       in the PhoneUs Component.
 */

import React, { useEffect, useState } from 'react';
import { isExperienceEditorActive } from '@sitecore-jss/sitecore-jss-react';
import { uid } from 'react-uid';

import ExperienceEditorContent from 'core/sitecore/experience-editor/ExperienceEditorContent';
import ExperienceEditorMessage from 'core/sitecore/experience-editor/ExperienceEditorMessage';
import Cta from 'core/components/Cta';
import RoutableSitecoreLink from 'core/components/RoutableSitecoreLink';
import { useTrackEvent } from 'core/hooks';
import { isInSitecorePreviewOrEditMode } from 'core/utils/global-utils';
import { triggerLivePersonWindow } from 'jss-components/t25-live-person/LivePerson';
import ContactMethod, {
	Title as ContactMethodTitle,
	Text as ContactMethodText,
} from './subcomponents/ContactMethod';
import { IFields } from '../../Footer.sitecore';
import CONTENT from './ContactUsBar.content';
import * as S from './ContactUsBar.style';

interface IProps {
	fields: IFields;
}

const ContactUsBar = ({ fields }: IProps) => {
	// ------------------------------
	// ------------------------------
	// Message (LivePerson click-to-chat)

	// State management for the below bind (to listen for when LivePerson
	// considers itself 'active') is contained in this component as it's not
	// used elsewhere. If it were, this logic ought to be hoisted to _AppContainer
	// in the AppContext. Note that core LivePerson logic (and the EmbeddedEngagement
	// tag this snipper depends-on/listens-to) is found at `core/vendor/LivePerson`.
	const [isLivePersonOnline, setIsLivePersonOnline] = useState(false);
	const [isLpTagLoaded, setIsLpTagLoaded] = useState(false);

	useEffect(() => {
		if (isExperienceEditorActive() || isInSitecorePreviewOrEditMode()) {
			console.warn('-------------------------------');
			console.warn(CONTENT.SITECORE.INACTIVE);
			console.warn('-------------------------------');
		} else if (!!window) {
			if (typeof window.lpTag === 'undefined') {
				console.warn('-------------------------------');
				console.warn(CONTENT.LP_TAG.DOESNT_EXIST);
				console.warn('-------------------------------');
			} else {
				// Check first if windows and lpTag is available serving the expected purpose
				const lpTagChecking =
					typeof window.lpTag === 'object' &&
					typeof window.lpTag.events === 'object' &&
					typeof window.lpTag.events.hasFired === 'function';

				let lpTagHasFired = lpTagChecking
					? window?.lpTag?.events?.hasFired('OFFER_DISPLAY')?.length
					: 0;
				let reps = 1;

				const fetchLpTagInterval = setInterval(() => {
					lpTagHasFired = lpTagChecking
						? window?.lpTag?.events?.hasFired('OFFER_DISPLAY')?.length
						: 0;

					if (lpTagHasFired > 0 || reps >= 50) {
						setIsLpTagLoaded(true);
						clearInterval(fetchLpTagInterval);
					} else {
						setIsLpTagLoaded(false);
					}
					reps += 1;
				}, 200);
			}
		}
	}, []);

	const _enableLiveChat = () => {
		const engagementId =
			(typeof window.lpTag.events === 'object' &&
				window?.lpTag?.events?.hasFired('OFFER_DISPLAY')[0]?.data
					?.engagementId) ||
			undefined;

		if (!!engagementId) {
			setIsLivePersonOnline(true);
		} else {
			setIsLpTagLoaded(false);
			setIsLivePersonOnline(false);
		}
	};

	useEffect(() => {
		if (isLpTagLoaded) {
			_enableLiveChat();
		}
	}, [isLpTagLoaded]);

	// ------------------------------

	/**
	 * Normalises date/month string values to always have padded zeroes
	 * where appropriate, so we get exact matches when comapring 'today'
	 * with values within the content-authorable `publicHolidays` array.
	 */
	const normaliseDate = (...date: string[]) =>
		date.map(ddMm => ddMm.padStart(2, '0')).join('/');

	const messageUsLink = fields.messageUsLink; // This is in fact text and NOT a link

	const messageUsIcon = fields.messageUsIcon.value;
	const onlineStatusMessage = fields.onlineStatusMessage;
	const offlineStatusMessage = fields.offlineStatusMessage;
	const publicHolidaysMessage = fields.publicHolidaysMessage;
	const publicHolidays = fields.publicHolidays.map(phObj => {
		const dd = phObj.fields.date.value;
		const mm = phObj.fields.month.value;

		return normaliseDate(dd, mm);
	});

	// Cannot be done inline lest we get prop-type inference false-positives
	const messageUsLinkText = messageUsLink.value;
	const onlineStatusMessageText = onlineStatusMessage.value;

	const date = new Date();
	const today = normaliseDate(
		date.getDate().toString(),
		(date.getMonth() + 1).toString(),
	);

	const isPublicHoliday = publicHolidays.includes(today);

	// Analytics spreadsheet:
	// - Section: 'Contact Us'
	const { trackEvent } = useTrackEvent(
		{
			event_section: 'navigation',
			event_type: 'contact us',
			event_title: '', // provided later
		},
		false,
	);

	const MessageUs = () => (
		<>
			<S.MessageUsButton
				type="button"
				disabled={!isLivePersonOnline}
				aria-label={CONTENT.LIVE_PERSON.BUTTON}
				data-target-id="footer--authenticated--live-person"
				onClick={() => {
					triggerLivePersonWindow();
					// Analytics spreadsheet:
					// - Section: 'Contact Us'
					// - ID: 1.3.1
					trackEvent({
						event_title: 'message us',
						event_label: messageUsLinkText,
						event_message: onlineStatusMessageText,
						event_trigger: 'click button',
					});
				}}
			>
				<ContactMethod icon={messageUsIcon} isActive={isLivePersonOnline}>
					<ContactMethodTitle
						jssText={messageUsLink}
						isActive={isLivePersonOnline}
						tag="h4"
					/>
					<ContactMethodText
						content={
							isPublicHoliday
								? publicHolidaysMessage
								: isLivePersonOnline
								? onlineStatusMessage
								: offlineStatusMessage
						}
					/>

					{/* Explaining LivePerson won't work in the Experience Editor */}
					<ExperienceEditorMessage
						message={CONTENT.SITECORE.EE.LIVE_PERSON_INACTIVE}
						position={{
							top: '0',
							left: '0',
						}}
					/>
				</ContactMethod>
			</S.MessageUsButton>

			{/* Ease of authoring for conditionally hidden JSS text */}
			<ExperienceEditorContent
				margin="5px 0 0"
				title={CONTENT.SITECORE.EE.STATUS_MESSAGES}
			>
				<ContactMethodText content={onlineStatusMessage} />
				<ContactMethodText content={publicHolidaysMessage} />
			</ExperienceEditorContent>
		</>
	);

	// ------------------------------
	// Phone

	const phoneIcon = fields.phoneIcon.value;
	const phoneNumberLink = fields.phoneNumber;
	const openingTimes = fields.openingTimes;

	// Cannot be done inline lest we get prop-type inference false-positives
	const phoneNumberLinkText = phoneNumberLink.value.text;
	const phoneNumberLinkUrl = phoneNumberLink.value.url;
	const openingTimesText = openingTimes.value;
	// Override href attribute with url value, please read top comment to see why
	const phoneNumberLinkProps = {
		...phoneNumberLink.value,
		href: phoneNumberLinkUrl,
	};

	const PhoneUs = () => (
		<Cta
			{...phoneNumberLinkProps}
			ctaType="unstyled"
			data-target-id="footer--authenticated--contact-link"
			onClick={() => {
				// Analytics spreadsheet:
				// - Section: 'Contact Us'
				// - ID: 1.3.3
				trackEvent({
					event_title: 'phone us',
					event_label: openingTimesText,
					event_message: '',
					event_trigger: 'click link',
					event_url: phoneNumberLinkUrl,
				});
			}}
		>
			<ContactMethod icon={phoneIcon}>
				<ContactMethodTitle>
					{isExperienceEditorActive() || isInSitecorePreviewOrEditMode() ? (
						<RoutableSitecoreLink field={phoneNumberLink} />
					) : (
						phoneNumberLinkText
					)}
				</ContactMethodTitle>
				<ContactMethodText content={openingTimes} />
			</ContactMethod>
		</Cta>
	);

	// ------------------------------
	// Email

	const emailIcon = fields.emailIcon.value;
	const emailLink = fields.emailLink;
	const emailUsAdditionalText = fields.emailUsAdditionalText;

	// Cannot be done inline lest we get prop-type inference false-positives
	const emailLinkProps = emailLink.value;
	const emailLinkText = emailLink.value.text;
	const emailLinkHref = emailLink.value.href;
	const emailUsAdditionalTextValue = emailUsAdditionalText.value;

	const EmailUs = () => (
		<Cta
			{...emailLinkProps}
			ctaType="unstyled"
			data-target-id="footer--authenticated--contact-link"
			onClick={() => {
				// Analytics spreadsheet:
				// - Section: 'Contact Us'
				// - ID: 1.3.2
				trackEvent({
					event_title: 'email us',
					event_label: emailUsAdditionalTextValue,
					event_message: '',
					event_trigger: 'click link',
					event_url: emailLinkHref,
				});
			}}
		>
			<ContactMethod icon={emailIcon}>
				<ContactMethodTitle>
					{isExperienceEditorActive() || isInSitecorePreviewOrEditMode() ? (
						<RoutableSitecoreLink field={emailLink} />
					) : (
						emailLinkText
					)}
				</ContactMethodTitle>
				<ContactMethodText content={emailUsAdditionalText} />
			</ContactMethod>
		</Cta>
	);

	// ------------------------------
	// Other contact methods

	const otherContactMethodsIcon = fields.otherContactMethodsIcon.value;
	const otherContactMethodsLink = fields.otherContactMethodsLink;
	const otherContactMethodsAdditionalText =
		fields.otherContactMethodsAdditionalText;

	// Cannot be done inline lest we get prop-type inference false-positives
	const otherContactMethodsLinkProps = otherContactMethodsLink.value;
	const otherContactMethodsLinkText = otherContactMethodsLink.value.text;
	const otherContactMethodsLinkHref = otherContactMethodsLink.value.href;
	const otherContactMethodsAdditionalTextValue =
		otherContactMethodsAdditionalText.value;

	const OtherContactMethods = () => (
		<Cta
			{...otherContactMethodsLinkProps}
			ctaType="unstyled"
			data-target-id="footer--authenticated--contact-link"
			onClick={() => {
				// Analytics spreadsheet:
				// - Section: 'Contact Us'
				// - ID: 1.3.4
				trackEvent({
					event_section: 'navigation',
					event_type: 'contact us',
					event_title: 'contact us',
					event_label: otherContactMethodsAdditionalTextValue,
					event_message: '',
					event_trigger: 'click link',
					event_url: otherContactMethodsLinkHref,
				});
			}}
		>
			<ContactMethod icon={otherContactMethodsIcon}>
				<ContactMethodTitle>
					{isExperienceEditorActive() || isInSitecorePreviewOrEditMode() ? (
						<RoutableSitecoreLink field={otherContactMethodsLink} />
					) : (
						otherContactMethodsLinkText
					)}
				</ContactMethodTitle>
				<ContactMethodText content={otherContactMethodsAdditionalText} />
			</ContactMethod>
		</Cta>
	);

	// ------------------------------

	return (
		<S.List>
			{[MessageUs, EmailUs, PhoneUs, OtherContactMethods].map(ContactMethod => (
				<li key={uid(ContactMethod)}>
					<ContactMethod />
				</li>
			))}
		</S.List>
	);
};

export default ContactUsBar;
